import { cached } from '@glimmer/tracking';

import moment from 'moment';

import { ApiMetricBuilderWizardStep } from 'webapp/enums/enums';
import { replaceAll } from 'webapp/utils/common';
import { isNumber } from 'webapp/utils/number';

export type RowType =
	| ApiMetricBuilderWizardStep.METRIC
	| ApiMetricBuilderWizardStep.DATE
	| ApiMetricBuilderWizardStep.DIMENSION;

export enum ValueType {
	NONE = '',
	NUMBER = 'number',
	STRING = 'string',
	DATE = 'date',
}

export default class JSONStructureRow {
	private type = ValueType.NONE;

	// given from outside
	string: string;
	index: number;

	// set in parseRow
	isValid = true;
	isArrayStart = false;
	isObjectKey = false;
	tabCount = 0; // identation depth
	tablessString = '';
	objectName = ''; // if row is object entry, this is the property name
	objectValue: string | null = null; // if row is object entry, this is the property value -- always string bcos extracted from string
	isResultArray = false; // if this row was selected, would result be an array
	pathChunks: string[] = [];

	// set in JSONStucture when creating rows
	parentRow: JSONStructureRow | null = null;

	constructor(
		json: SomeDataObject,
		rowString: string,
		rowIndex: number,
		previousRows: JSONStructureRow[],
	) {
		this.index = rowIndex;
		this.string = rowString;
		// previousRows is already reversed (previous row to this is in index 0)
		this.parseRow(json, rowString, previousRows);
	}

	@cached
	get path(): string {
		return this.pathChunks.join('.');
	}

	get objectKey(): string {
		return this.isObjectKey ? this.objectName : '';
	}

	get escapedObjectKey() {
		return JSONStructureRow.escapePathChunk(this.objectKey);
	}

	get isString(): boolean {
		return this.type === ValueType.STRING;
	}

	get isNumber(): boolean {
		return this.type === ValueType.NUMBER;
	}

	get isDate(): boolean {
		if (this.type === ValueType.DATE) {
			return true;
		}
		// unix timestamp maybe? allow dates for -20 years and +10 years
		const objValue = this.objectValue;
		if (objValue && isNumber(objValue) && objValue.length === 10) {
			const m = moment.unix(parseInt(objValue)),
				valueYear = m.year(),
				currentYear = new Date().getFullYear();
			return currentYear - 20 <= valueYear && currentYear + 10 >= valueYear;
		}
		return false;
	}

	checkValidityForDataType(type: RowType, strict = false): boolean {
		// with strict flag, dont allow unix timestamps for numbers
		// and dont allow just any strings for dates
		// strict flag is used only for autoselecting suggested rows
		switch (type) {
			case ApiMetricBuilderWizardStep.METRIC:
				return this.isNumber && (!strict || !this.isDate);
			case ApiMetricBuilderWizardStep.DIMENSION:
				return this.isString;
			case ApiMetricBuilderWizardStep.DATE:
				return this.isDate || (!strict && this.isString);
		}
	}

	private parseRow(
		json: SomeDataObject,
		rowString: string,
		previousRows: JSONStructureRow[],
	): void {
		this.tabCount = JSONStructureRow.getTabCount(rowString);
		this.tablessString = replaceAll(rowString, '\t', '');

		this.isArrayStart = rowString.endsWith('[');

		if (['[', ']', '],', '{', '}', '},'].includes(this.tablessString)) {
			this.isValid = false;
			return;
		}

		const paths: string[] = [];

		let valueForTypeCheck = null;

		this.objectName =
			JSONStructureRow.extractObjectPartFromRowString(this.tablessString, true) || '';

		if (this.isArrayStart || rowString.endsWith('{')) {
			this.isObjectKey = true;
			valueForTypeCheck = this.objectKey;
		} else {
			if (this.objectName) {
				paths.push(this.objectName);
				this.objectValue = JSONStructureRow.extractObjectPartFromRowString(
					this.tablessString,
				);
				valueForTypeCheck = this.objectValue;
			} else {
				this.isValid = false;
				return;
			}
		}

		const isJsonArray = Array.isArray(json);

		let isResultArray = isJsonArray; // if outer is array, result is always array

		// checking from selection up
		let nextImportantRowTabCount = this.tabCount - 1;
		previousRows.forEach((row, idx) => {
			if (row.tabCount > nextImportantRowTabCount) {
				return;
			}

			const tablessString = row.tablessString;
			if (tablessString === '{') {
				// if only curly, it's either root or array element
				if (row.tabCount === 0) {
					return;
				} else if (!isJsonArray) {
					// the array key is in the previous row
					const prevRow = previousRows[idx + 1];
					if (prevRow?.string.endsWith('[')) {
						isResultArray = true;
						const pathPiece = prevRow.objectName;
						if (pathPiece) {
							paths.push(pathPiece);
						} else {
							this.isValid = false;
							return;
						}
						nextImportantRowTabCount -= 2;
					}
				}
				return;
			} else if (tablessString.endsWith('{')) {
				const pathPiece = row.objectName;
				if (pathPiece) {
					paths.push(pathPiece);
					nextImportantRowTabCount -= 1;
				} else {
					this.isValid = false;
				}
			}
		});

		if (valueForTypeCheck !== null) {
			if (isNumber(valueForTypeCheck)) {
				this.type = ValueType.NUMBER;
			} else {
				// string, maybe date?
				if (moment(valueForTypeCheck).isValid()) {
					this.type = ValueType.DATE;
				} else {
					this.type = ValueType.STRING;
				}
			}
		}

		this.pathChunks = paths.reverse().map((chunk) => {
			return JSONStructureRow.escapePathChunk(chunk);
		});

		this.isResultArray = isResultArray;
	}

	private static escapePathChunk(chunk: string): string {
		// handling path names that include dot or hyphen (escape with backticks) - https://app.asana.com/1/7582066485656/task/1211393420282359
		if (chunk.includes('.') || chunk.includes('-')) {
			return `\`${chunk}\``;
		}
		return chunk;
	}

	private static getTabCount(row: string): number {
		let tabCount = 0;
		row.split('\t').some((chunk) => {
			if (!chunk) {
				tabCount++;
			}
			return !!chunk;
		});
		return tabCount;
	}

	private static extractObjectPartFromRowString(
		tablessRowString: string,
		beforeColon = false,
	): string | null {
		let valueString = tablessRowString.split(':')[beforeColon ? 0 : 1];
		if (typeof valueString === 'undefined') {
			return null;
		}
		valueString = valueString.trim();
		if (valueString.endsWith(',')) {
			// remove ending comma
			valueString = valueString.substring(0, valueString.length - 1);
		}
		if (valueString.startsWith('"') && valueString.endsWith('"')) {
			// remove quotes
			valueString = valueString.substring(1, valueString.length - 1);
		}
		return valueString;
	}
}
